
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileDown, FilePlus } from 'lucide-react';

// Indianized sample data
const sampleLedgerEntries = [
  { id: 1, date: '2025-04-15', accountCode: '1300', description: 'Loan issued to Adani Enterprises', debit: 75000000, credit: 0, reference: 'LN-2025-001' },
  { id: 2, date: '2025-04-15', accountCode: '1000', description: 'Cash payment for loan issuance', debit: 0, credit: 75000000, reference: 'LN-2025-001' },
  { id: 3, date: '2025-04-30', accountCode: '1310', description: 'Accrued interest on loan asset', debit: 312500, credit: 0, reference: 'LN-2025-002' },
  { id: 4, date: '2025-04-30', accountCode: '4200', description: 'Interest income on loans', debit: 0, credit: 312500, reference: 'LN-2025-002' },
  { id: 5, date: '2025-05-01', accountCode: '1300', description: 'Loan issued to Reliance Industries', debit: 100000000, credit: 0, reference: 'LN-2025-003' },
  { id: 6, date: '2025-05-01', accountCode: '1000', description: 'Cash payment for loan issuance', debit: 0, credit: 100000000, reference: 'LN-2025-003' },
];

const LoansLedger = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Loans Ledger</h2>
          <p className="text-muted-foreground">View and manage accounting entries for loans (assets)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="flex items-center gap-2">
            <FileDown className="h-4 w-4" />
            Export for Tally
          </Button>
          <Button className="flex items-center gap-2">
            <FilePlus className="h-4 w-4" />
            New Entry
          </Button>
        </div>
      </div>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Ledger Entries</CardTitle>
          <CardDescription>All loan asset-related accounting transactions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Period:</span>
              <Select defaultValue="current-month">
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current-month">Current Month</SelectItem>
                  <SelectItem value="previous-month">Previous Month</SelectItem>
                  <SelectItem value="quarter">Current Quarter</SelectItem>
                  <SelectItem value="year-to-date">Year to Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <Select defaultValue="all">
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entries</SelectItem>
                  <SelectItem value="posted">Posted to Tally</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Table>
            <TableCaption>List of loan asset accounting entries</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Debit (₹)</TableHead>
                <TableHead className="text-right">Credit (₹)</TableHead>
                <TableHead>Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sampleLedgerEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.accountCode}</TableCell>
                  <TableCell>{entry.description}</TableCell>
                  <TableCell className="text-right">
                    {entry.debit > 0 ? entry.debit.toLocaleString('en-IN') : ''}
                  </TableCell>
                  <TableCell className="text-right">
                    {entry.credit > 0 ? entry.credit.toLocaleString('en-IN') : ''}
                  </TableCell>
                  <TableCell>{entry.reference}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoansLedger;
